// src/components/Services.jsx
import React from "react";
import styled, { keyframes, useTheme } from "styled-components";
import { Section } from "./sharedStyles";
import { Card as BaseCard } from "./sharedStyles";
import { CreditCard, Truck, BarChart2 } from "lucide-react";
import ContactModal from "./ContactModal";

/* ---- animations ---- */
const floatUp = keyframes`
  from { transform: translateY(10px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

/* ---- layout & styles (theme-aware) ---- */
const Container = styled(Section)`
  padding-top: 40px;
  padding-bottom: 48px;
  text-align: center;
  position: relative;
  overflow-x: hidden;
  overflow-y: visible;
  background: ${(p) =>
    p.theme.gradients?.servicesBg ||
    `linear-gradient(180deg, ${p.theme.colors?.bgDarker || "#02050A"} 0%, ${
      p.theme.colors?.bgDark || "#0A0F1D"
    } 100%)`};
  color: ${(p) => p.theme.colors?.text || "#FFFFFF"};

  &::before {
    content: "";
    position: absolute;
    inset: -28% -8% 60% -8%;
    height: 420px;
    background: radial-gradient(
        circle at 8% 12%,
        ${(p) => p.theme.colors?.accent || "#4A00E0"}12 0%,
        transparent 26%
      ),
      radial-gradient(
        circle at 92% 80%,
        ${(p) => p.theme.colors?.secondary || "#E63946"}10 0%,
        transparent 26%
      );
    z-index: 0;
    pointer-events: none;
    filter: blur(36px) saturate(1.05);
    opacity: 0.9;
  }

  @media (min-width: 520px) {
    padding-top: 56px;
    padding-bottom: 64px;
  }
`;

const Heading = styled.h2`
  margin: 0 0 12px 0;
  color: ${(p) => p.theme.colors?.text || "#FFFFFF"};
  position: relative;
  z-index: 2;
`;

const Sub = styled.p`
  margin: 0 0 20px 0;
  color: ${(p) => p.theme.colors?.muted || "#A7B1C2"};
  max-width: 760px;
  margin-left: auto;
  margin-right: auto;
  position: relative;
  z-index: 2;
`;

const Grid = styled.div`
  display: grid;
  gap: 18px;
  grid-template-columns: 1fr;
  padding: 6px;
  max-width: 1100px;
  margin: 24px auto 0;
  position: relative;
  z-index: 2;

  @media (min-width: 720px) {
    grid-template-columns: repeat(3, 1fr);
    padding: 8px;
    gap: 20px;
  }
`;

const ServiceCard = styled(BaseCard)`
  background: ${(p) =>
    p.theme.colors?.cardDark ||
    `linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0.01))`};
  border: 1px solid
    ${(p) => p.theme.colors?.cardBorderDark || "rgba(255,255,255,0.03)"};
  color: ${(p) => p.theme.colors?.text || "#fff"};
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 240px;
  padding: 26px;
  transition: transform 260ms cubic-bezier(0.2, 0.9, 0.2, 1),
    box-shadow 260ms ease;
  transform-origin: center;
  animation: ${floatUp} 520ms ease both;
  animation-delay: calc(var(--i, 0) * 80ms);
  will-change: transform, box-shadow;
  align-items: center;
  text-align: center;
  border-radius: 14px;
  box-shadow: 0 10px 28px rgba(2, 6, 23, 0.5);

  &:hover,
  &:focus-within {
    transform: translateY(-10px) scale(1.01);
    box-shadow: 0 36px 84px rgba(2, 6, 23, 0.64);
  }

  &:focus-within {
    outline: 3px solid
      ${(p) =>
        p.theme.colors?.primary ? `${p.theme.colors.primary}22` : "#F5C54222"};
    outline-offset: 6px;
  }

  @media (min-width: 520px) {
    padding: 28px;
    min-height: 250px;
  }
`;

const TopRow = styled.div`
  display: flex;
  gap: 14px;
  align-items: center;
  flex-direction: column;
  justify-content: center;

  @media (min-width: 720px) {
    flex-direction: row;
    align-items: flex-start;
  }
`;

const Title = styled.div`
  font-weight: 800;
  font-size: 1.06rem;
  line-height: 1.18;
`;

const Desc = styled.div`
  color: ${(p) => p.theme.colors?.muted || "#A7B1C2"};
  font-size: 0.99rem;
  line-height: 1.6;
  max-width: 520px;
`;

const Footer = styled.div`
  margin-top: auto;
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
`;

const CTA = styled.a`
  text-decoration: none;
  padding: 10px 14px;
  border-radius: 12px;
  background: ${(p) =>
    p.primary
      ? p.theme.gradients?.cta || `linear-gradient(90deg,#E63946,#FF3C2F)`
      : "transparent"};
  color: ${(p) =>
    p.primary
      ? p.theme.colors?.textOnCta || "#ffffff"
      : p.theme.colors?.accent || "#4A00E0"};
  font-weight: 800;
  box-shadow: ${(p) =>
    p.primary ? "0 14px 36px rgba(230,57,70,0.12)" : "none"};
  border: ${(p) =>
    p.primary
      ? "none"
      : `1px solid ${p.theme.colors?.muted || "rgba(167,177,194,0.12)"}`};
  transition: transform 160ms ease, box-shadow 160ms ease, opacity 140ms ease;
  display: inline-flex;
  gap: 10px;
  align-items: center;
  font-size: 0.95rem;

  &:hover {
    transform: translateY(-4px);
    opacity: 0.98;
    box-shadow: ${(p) =>
      p.primary ? "0 20px 48px rgba(230,57,70,0.16)" : "none"};
  }
  &:focus {
    outline: 3px solid
      ${(p) =>
        p.theme.colors?.primary ? `${p.theme.colors.primary}22` : "#F5C54222"};
    outline-offset: 4px;
  }

  svg {
    opacity: 0.98;
  }
`;

const Tag = styled.span`
  background: ${(p) => p.theme.colors?.tagBg || "rgba(255,255,255,0.03)"};
  color: ${(p) => p.theme.colors?.accent || "#4A00E0"};
  padding: 6px 10px;
  border-radius: 999px;
  font-weight: 800;
  font-size: 0.82rem;
`;

/* ---- services data (icons assigned) ---- */
const SERVICES = [
  {
    title: "Early Stage Collections",
    desc: "Gentle reminders, flexible restructuring conversations, and proactive outreach to prevent delinquency.",
    tag: "Pre-NPA",
    accentKey: "primary",
    iconKey: "primary",
    Icon: CreditCard,
  },
  {
    title: "Late Stage Recovery",
    desc: "Field operations, trained recovery teams and coordinated repossession & legal escalation support.",
    tag: "Recovery",
    accentKey: "secondary",
    iconKey: "secondary",
    Icon: Truck,
  },
  {
    title: "NPA Management",
    desc: "Bucket-wise segmentation, strategy mapping and MIS for efficient NPA resolution.",
    tag: "NPA",
    accentKey: "accent",
    iconKey: "accent",
    Icon: BarChart2,
  },
];

/* ---- component ---- */
export default function Services() {
  const theme = useTheme();
  const [open, setOpen] = React.useState(false);
  return (
    <Container id="services" aria-labelledby="services-heading">
      <Heading id="services-heading">Our Services</Heading>
      <Sub>
        Complete lifecycle collections & recovery solutions tailored for
        financial institutions.
      </Sub>

      <Grid>
        {SERVICES.map((s, i) => {
          const iconColor =
            (s.iconKey && theme.colors && theme.colors[s.iconKey]) ||
            theme.colors?.accent ||
            "#4A00E0";

          const Icon = s.Icon;

          return (
            <ServiceCard
              key={s.title}
              aria-labelledby={`svc-${i}`}
              tabIndex={0}
              style={{ "--i": i }}
            >
              <TopRow>
                {/* <IconBadge bg={accent} aria-hidden> */}
                <Icon size={28} strokeWidth={1.6} color={iconColor} />
                {/* </IconBadge> */}

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Title id={`svc-${i}`}>{s.title}</Title>
                  <div style={{ marginTop: 10 }}>
                    <Tag>{s.tag}</Tag>
                  </div>
                </div>
              </TopRow>

              <Desc>{s.desc}</Desc>

              <Footer>
                <CTA
                  href="#contact"
                  primary
                  aria-label={`Contact Us For ${s.title}`}
                  onClick={() => {
                    setOpen(true);
                  }}
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden
                  >
                    <path
                      d="M5 12h14"
                      stroke="rgba(255,255,255,0.9)"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M13 5l7 7-7 7"
                      stroke="rgba(255,255,255,0.9)"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Contact Us
                </CTA>

                <CTA href="#about" aria-label={`Learn more about ${s.title}`}>
                  Learn more
                </CTA>
              </Footer>
            </ServiceCard>
          );
        })}
      </Grid>
      <ContactModal
        open={open}
        onClose={() => setOpen(false)}
        phone="+918510040060"
        email="Ksgm012020@gmail.com"
      />
    </Container>
  );
}
